import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import UploadInput from "@/features/file-upload/components/upload-input";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicOrganisation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateOrganisation } from "../api/update-organisation";
import {
  OrganisationBrandingFormData,
  organisationBrandingSchema,
} from "../types/organisation-branding";

type OrganisationBrandingFormProps = {
  initialData: PublicOrganisation;
};

function apiDataToFormData(
  data: PublicOrganisation,
): OrganisationBrandingFormData {
  return {
    logoURL: data.logoURL ?? "",
  };
}

export function OrganisationBrandingForm(props: OrganisationBrandingFormProps) {
  const { initialData } = props;
  const form = useForm<OrganisationBrandingFormData>({
    resolver: zodResolver(organisationBrandingSchema),
    defaultValues: apiDataToFormData(initialData),
  });
  const { mutateAsync: updateOrganisation } = useUpdateOrganisation({
    onSuccess: (result) => {
      form.reset(apiDataToFormData(result));
      queryClient.setQueryData(["organisation", result.id], result);
      queryClient.setQueryData(
        ["organisations"],
        (oldData: PublicOrganisation[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((org) => (org.id === result.id ? result : org));
        },
      );
    },
  });

  async function handleUpdateOrganisation(data: OrganisationBrandingFormData) {
    try {
      await updateOrganisation({ id: initialData.id, organisation: data });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateOrganisation)}>
          <Card.Header title="Branding" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="logoURL"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logo</FormLabel>
                  <FormDescription>
                    You can upload .jpeg, .png, .gif, or .webp files.
                  </FormDescription>
                  <FormControl>
                    <UploadInput
                      name="logoURL"
                      onChange={field.onChange}
                      value={field.value}
                      allowedFileTypes={[
                        "image/jpeg",
                        "image/png",
                        "image/gif",
                        "image/webp",
                      ]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset(apiDataToFormData(initialData))}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
